
import {Injectable, NgZone} from '@angular/core';
import {Observable} from "rxjs";
import {EventSourcePolyfill} from 'event-source-polyfill';
import {AuthService} from "../../shared/services/auth.service";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class ChatService {
    private endpoints = environment.endpoints;

    constructor(
        private zone: NgZone,
        private authService: AuthService,
        private http: HttpClient
    ) { }

    getInstalledModels(): Observable<string[]> {
        return this.http.get<string[]>(`${this.endpoints.models}`);
    }

    getDefaultModel(): Observable<string> {
        return this.http.get(`${this.endpoints.modelsDefault}`, { responseType: 'text' });
    }

    streamChat(message: string, model?: string): Observable<string> {
      return new Observable<string>((observer) => {
        const token = this.authService.getToken();
        let url = `${this.endpoints.chat}?chatRequest=${encodeURIComponent(message)}`;
        if (model) {
          url += `&model=${encodeURIComponent(model)}`;
        }
        const supportsStreaming = typeof fetch === 'function' && typeof ReadableStream !== 'undefined';

        if (supportsStreaming) {
          const controller = new AbortController();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;

          fetch(url, {
            method: 'GET',
            headers,
            cache: 'no-cache',
            signal: controller.signal
          })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`SSE request failed: ${response.status} ${response.statusText}`);
              }
              const body = response.body;
              if (!body) {
                throw new Error('SSE response has no body');
              }

              const reader = body.getReader();
              const decoder = new TextDecoder('utf-8');
              let buffer = '';
              let eventName = '';
              let dataLines: string[] = [];

              const dispatchEvent = () => {
                const data = dataLines.join('\n');
                dataLines = [];
                const name = eventName || 'message';
                eventName = '';

                if (data === '[DONE]') {
                  this.zone.run(() => observer.complete());
                  controller.abort();
                  return false; // stop further processing
                }

                if (name === 'message' || name === 'chat-message') {
                  this.zone.run(() => observer.next(data));
                }
                return true;
              };

              const processText = (text: string) => {
                buffer += text;
                let idx: number;
                while ((idx = buffer.indexOf('\n')) !== -1) {
                  let line = buffer.slice(0, idx);
                  buffer = buffer.slice(idx + 1);
                  if (line.endsWith('\r')) line = line.slice(0, -1);

                  if (line.startsWith('event:')) {
                    eventName = line.slice(6).trim();
                  } else if (line.startsWith('data:')) {
                    // Preserve leading spaces after 'data:' by not trimming anything beyond the prefix
                    dataLines.push(line.slice(5));
                  } else if (line === '') {
                    // Blank line indicates event dispatch
                    const keepGoing = dispatchEvent();
                    if (!keepGoing) return false;
                  }
                }
                return true;
              };

              const pump = (): any =>
                reader
                  .read()
                  .then(({ done, value }) => {
                    if (done) {
                      // Flush any pending event
                      if (buffer.length > 0) processText('\n');
                      this.zone.run(() => observer.complete());
                      return;
                    }

                    const chunkText = decoder.decode(value, { stream: true });
                    const keepGoing = processText(chunkText);
                    if (keepGoing) return pump();
                  })
                  .catch((err) => {
                    this.zone.run(() => observer.error(err));
                  });

              return pump();
            })
            .catch((err) => {
              // If fetch streaming fails for any reason, surface the error
              this.zone.run(() => observer.error(err));
            });

          return () => controller.abort();
        }

        // Fallback to EventSource if streaming fetch is not supported
        const eventSource = new EventSourcePolyfill(url, {
          method: 'GET',
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        } as any);

        const handleMessage = (event: MessageEvent) => {
          if (event.data === '[DONE]') {
            this.zone.run(() => {
              observer.complete();
            });
            eventSource.close();
            return;
          }
          this.zone.run(() => {
            observer.next(event.data);
          });
        };

        eventSource.onmessage = handleMessage;
        eventSource.addEventListener('chat-message', handleMessage as any);

        eventSource.onerror = (err: any) => {
          const CLOSED = 2;
          if ((eventSource as any).readyState === CLOSED) {
            this.zone.run(() => observer.complete());
          } else {
            this.zone.run(() => observer.error(err));
          }
          eventSource.close();
        };

        return () => eventSource.close();
      });
    }
}
